import { DEMO_SCENARIOS } from './scenarios.js';

/**
 * License-wide workfront-objects fixture. The SPA's scenarios list
 * reads `/v1/scenarios/workfront-objects-data` (a flat license-scoped
 * aggregate) to render the expand-row + the "Workfront Objects (All)"
 * tab. Operator-reported gap: the per-scenario fixtures already carried
 * `workfrontObjects`, but the **list page** reads from this separate
 * aggregate endpoint, so demo mode showed empty rows.
 *
 * This fixture flattens the per-scenario `workfrontObjects` arrays from
 * `scenarios.ts` into the same flat shape the engine returns:
 *
 *   {
 *     data: [
 *       {id, workfrontId, fusionScenarioId, fusionModuleId, ...},
 *       ...
 *     ]
 *   }
 *
 * Engine route: `GET /engine/v1/scenarios/workfront-objects-data`
 * (`handlers/scenarios.ts > workfrontObjectsData`). App-API proxies
 * verbatim. Shape consumed by `useScenarioWorkfrontObjects` in
 * `apps/wf-coe-app-spa/src/features/scenario/hooks.ts:265`.
 */
interface WorkfrontObjectRow {
  id: number;
  workfrontId: string;
  fusionModuleId: string;
  fusionModuleType: string;
  fusionModuleName: string;
  fusionScenarioId: string;
  workfrontType: string | null;
  workfrontLink: string | null;
  workfrontName: string | null;
  workfrontTypeLabel: string | null;
}

interface ScenarioObjectRecord {
  id?: number;
  workfrontId?: string;
  workfrontType?: string;
  workfrontTypeLabel?: string;
  workfrontLink?: string;
  workfrontName?: string;
  fusionModuleId?: number | string;
  fusionModuleName?: string;
  fusionModuleType?: string;
}

function flatten(): WorkfrontObjectRow[] {
  const rows: WorkfrontObjectRow[] = [];
  for (const scenario of DEMO_SCENARIOS) {
    const fusionScenarioId = (scenario.fusionId as string | undefined) ?? '';
    const objects = scenario.workfrontObjects;
    if (!fusionScenarioId || !Array.isArray(objects)) continue;
    for (const raw of objects as ScenarioObjectRecord[]) {
      rows.push({
        id: typeof raw.id === 'number' ? raw.id : 0,
        workfrontId: typeof raw.workfrontId === 'string' ? raw.workfrontId : '',
        fusionModuleId:
          typeof raw.fusionModuleId === 'number' || typeof raw.fusionModuleId === 'string'
            ? String(raw.fusionModuleId)
            : '',
        fusionModuleType:
          typeof raw.fusionModuleType === 'string' ? raw.fusionModuleType : 'workfront',
        fusionModuleName: typeof raw.fusionModuleName === 'string' ? raw.fusionModuleName : '',
        fusionScenarioId,
        workfrontType: typeof raw.workfrontType === 'string' ? raw.workfrontType : null,
        workfrontLink: typeof raw.workfrontLink === 'string' ? raw.workfrontLink : null,
        workfrontName: typeof raw.workfrontName === 'string' ? raw.workfrontName : null,
        workfrontTypeLabel:
          typeof raw.workfrontTypeLabel === 'string' ? raw.workfrontTypeLabel : null,
      });
    }
  }
  return rows;
}

/**
 * Eagerly-computed flat list — populated once at import time. The
 * fixture is a pure derivation from `DEMO_SCENARIOS`, so this stays
 * in lockstep with the scenario fixture automatically; no second
 * source of truth.
 */
export const DEMO_WORKFRONT_OBJECTS: ReadonlyArray<WorkfrontObjectRow> = flatten();
